import { Step1WritingMode } from '../types/step1Writing';

export interface Step1PathOption {
  mode: Step1WritingMode;
  title: string;
  description: string;
}

export interface Step1MethodStep {
  id: string;
  title: string;
  body: string;
}

export const STEP1_REVIEW_STEP_BODY =
  'Stay in contact with your sponsor and share your First Step story to be reviewed when you need help and before you finally present it.';

/** Order shown on the path picker (Guided first). */
export const STEP1_PATH_OPTIONS: Step1PathOption[] = [
  {
    mode: 'wizard',
    title: 'Guided walkthrough',
    description: 'Step-by-step prompts, then assemble your words into a draft.',
  },
  {
    mode: 'typed',
    title: 'Type it here',
    description: 'Plain-text editor with auto-save in Recovery Commons.',
  },
  {
    mode: 'upload',
    title: 'Upload a document',
    description:
      'Supported formats: .txt, .docx, .pdf, .md. Max size: 5 MB. Original file stored encrypted.',
  },
  {
    mode: 'external',
    title: 'Written and stored elsewhere',
    description: 'Pen and paper, another app, or already finished — track progress here.',
  },
];

export const STEP1_METHOD_STEPS: Record<Step1WritingMode, Step1MethodStep[]> = {
  wizard: [
    {
      id: 'walkthrough',
      title: 'Your story',
      body: 'Answer each prompt below as best you can — then click Next. Click Skip for prompts you don\'t have an answer for, aren\'t ready for, or already covered in a previous answer. These prompts will take you through all the parts of a typical First Step story. Take a break at any time — your entries are saved and you can start up where you left off next time.',
    },
    {
      id: 'assemble',
      title: 'Assemble your draft',
      body: 'Combine your walkthrough answers into one draft — your own words, in order.',
    },
    {
      id: 'presentation',
      title: 'Presentation plan',
      body: 'Set when and where you plan to share to help you and your sponsor prepare.',
    },
    {
      id: 'review',
      title: 'Review & share',
      body: STEP1_REVIEW_STEP_BODY,
    },
  ],
  typed: [
    {
      id: 'your-story',
      title: 'Your story',
      body: 'Type your story below (Insert template if an outline would help).',
    },
    {
      id: 'draft-checklist',
      title: 'Draft checklist',
      body: 'Optional reminders about common First Step topics — your story is never changed for you.',
    },
    {
      id: 'presentation',
      title: 'Presentation plan',
      body: 'Set when and where you plan to share to help you and your sponsor prepare.',
    },
    {
      id: 'review',
      title: 'Review & share',
      body: STEP1_REVIEW_STEP_BODY,
    },
  ],
  upload: [
    {
      id: 'upload',
      title: 'Upload your document',
      body: 'Upload your document (.txt, .docx, .pdf, or .md — max 5 MB).',
    },
    {
      id: 'review-edit',
      title: 'Review & edit',
      body: 'Review and edit the extracted text below if needed.',
    },
    {
      id: 'draft-checklist',
      title: 'Draft checklist',
      body: 'Optional reminders about common First Step topics — your story is never changed for you.',
    },
    {
      id: 'presentation',
      title: 'Presentation plan',
      body: 'Set when and where you plan to share to help you and your sponsor prepare.',
    },
    {
      id: 'review',
      title: 'Review & share',
      body: STEP1_REVIEW_STEP_BODY,
    },
  ],
  external: [
    {
      id: 'where-kept',
      title: 'Where you keep it',
      body: 'Note where you keep your story (optional) — on paper, another app, with your sponsor, etc.',
    },
    {
      id: 'presentation',
      title: 'Presentation plan',
      body: 'Set when and where you plan to share to help you and your sponsor prepare.',
    },
    {
      id: 'review',
      title: 'Review & share',
      body: STEP1_REVIEW_STEP_BODY,
    },
  ],
};

export const STEP1_ENCRYPTION_REMINDER =
  'Reminder: Story text and uploads are encrypted in Recovery Commons. Progress shares never include your story text.';

export function methodTitle(mode: Step1WritingMode): string {
  return STEP1_PATH_OPTIONS.find((o) => o.mode === mode)?.title ?? 'First Step writing';
}

export function methodStepByIndex(mode: Step1WritingMode, index: number): Step1MethodStep | undefined {
  return STEP1_METHOD_STEPS[mode][index];
}
